/** Legacy `services/map/cluster-engine.ts` 승격 (F-09 KEEP). */
export interface Clusterable { id: string; latitude: number; longitude: number }

export interface Cluster<T extends Clusterable> {
  kind: "CLUSTER";
  id: string;
  latitude: number;
  longitude: number;
  count: number;
  members: T[];
}
export interface Single<T extends Clusterable> { kind: "SINGLE"; item: T }
export type ClusterNode<T extends Clusterable> = Cluster<T> | Single<T>;

export interface ClusterOptions {
  /** 화면상 픽셀 반경 */
  radiusPx: number;
  /** 위경도 → 픽셀 환산 계수 (zoom 에서 파생) */
  pixelsPerDegree: number;
  /** 이 개수 미만이면 클러스터로 접지 않는다 */
  minPoints?: number;
}

/**
 * 그리드 스냅 기반 클러스터링. O(n).
 * 결정론적이어야 한다 — 같은 입력은 같은 출력을 낸다 (테스트 가능성).
 */
export function clusterPoints<T extends Clusterable>(
  items: readonly T[],
  opts: ClusterOptions,
): ClusterNode<T>[] {
  const minPoints = opts.minPoints ?? 2;
  if (opts.pixelsPerDegree <= 0 || opts.radiusPx <= 0) {
    return items.map((item) => ({ kind: "SINGLE", item }));
  }
  const cell = opts.radiusPx / opts.pixelsPerDegree;
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const gx = Math.floor(item.longitude / cell);
    const gy = Math.floor(item.latitude / cell);
    const key = `${gx}:${gy}`;
    const b = buckets.get(key);
    if (b) b.push(item); else buckets.set(key, [item]);
  }
  const out: ClusterNode<T>[] = [];
  for (const [key, members] of [...buckets.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
    if (members.length < minPoints) {
      for (const item of members) out.push({ kind: "SINGLE", item });
      continue;
    }
    let lat = 0, lng = 0;
    for (const m of members) { lat += m.latitude; lng += m.longitude; }
    out.push({
      kind: "CLUSTER",
      id: `c:${key}`,
      latitude: lat / members.length,
      longitude: lng / members.length,
      count: members.length,
      members,
    });
  }
  return out;
}
